import { useEffect, useRef, useState } from "react";
import { Link, NavLink, Outlet, useLocation } from "react-router-dom";
import footerLogoUrl from "../assets/growthwise-logo-reverse-education.svg";
import { useLanguage } from "../i18n/LanguageProvider";
import { useAuth } from "../state/AuthContext";
import { BrandLink } from "./Brand";
import { LanguageSwitcher } from "./LanguageSwitcher";

/**
 * Site chrome: header, main landmark and footer.
 *
 * Shared by every page, public and private, so navigation and the language
 * control behave identically throughout.
 */
export const Layout = () => {
  const { t, path } = useLanguage();
  const { status, isAdmin, signOut } = useAuth();
  const location = useLocation();

  const [menuOpen, setMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [scrollProgress, setScrollProgress] = useState(0);
  const toggleRef = useRef<HTMLButtonElement>(null);

  // A route change must close the drawer, otherwise it stays open over the
  // new page on a phone.
  useEffect(() => {
    setMenuOpen(false);
  }, [location.pathname]);

  /**
   * Moves focus to the top on navigation.
   *
   * A single-page app does not reset focus by itself, so a keyboard or
   * screen-reader user would otherwise stay where the previous page's link
   * was and have no idea the page changed. A link to a section of another
   * page lands on that section instead of the top.
   */
  useEffect(() => {
    const main = document.getElementById("main-content");
    if (main) {
      main.focus({ preventScroll: true });
      const anchor = location.hash
        ? document.getElementById(decodeURIComponent(location.hash.slice(1)))
        : null;
      if (anchor) {
        anchor.scrollIntoView({ block: "start" });
      } else {
        window.scrollTo({ top: 0, behavior: "auto" });
      }
    }
  }, [location.pathname, location.hash]);

  useEffect(() => {
    const onScroll = (): void => {
      const scrolling = document.documentElement;
      const max = scrolling.scrollHeight - scrolling.clientHeight;
      setScrollProgress(max > 0 ? Math.min(1, scrolling.scrollTop / max) : 0);
      setScrolled(window.scrollY > 8);
    };
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // Escape closes the drawer and returns focus to the button that opened it.
  useEffect(() => {
    if (!menuOpen) return undefined;
    const onKeyDown = (event: KeyboardEvent): void => {
      if (event.key === "Escape") {
        setMenuOpen(false);
        toggleRef.current?.focus();
      }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [menuOpen]);

  const publicLinks = [
    { to: "/", label: t("nav.home"), end: true },
    { to: "/about", label: t("nav.about") },
    { to: "/services", label: t("nav.services") },
    { to: "/check-in", label: t("nav.assessment") },
    { to: "/partnerships", label: t("nav.partnerships") },
    { to: "/contact", label: t("nav.contact") },
  ];

  const renderLinks = (onNavigate?: () => void) => (
    <>
      {publicLinks.map((link) => (
        <NavLink
          key={link.to}
          to={path(link.to)}
          end={link.end ?? false}
          className="gw-nav__link"
          onClick={onNavigate}
        >
          {link.label}
        </NavLink>
      ))}
      {status === "signed-in" ? (
        <>
          <NavLink
            to={path("/account")}
            className="gw-nav__link"
            onClick={onNavigate}
          >
            {t("nav.account")}
          </NavLink>
          {isAdmin ? (
            <NavLink
              to={path("/admin")}
              className="gw-nav__link"
              onClick={onNavigate}
            >
              {t("nav.admin")}
            </NavLink>
          ) : null}
          <button
            type="button"
            className="gw-btn gw-btn--ghost gw-btn--sm"
            onClick={() => {
              onNavigate?.();
              void signOut();
            }}
          >
            {t("nav.signOut")}
          </button>
        </>
      ) : (
        <Link
          to={path("/sign-in")}
          className="gw-btn gw-btn--secondary gw-btn--sm"
          onClick={onNavigate}
        >
          {t("nav.signIn")}
        </Link>
      )}
    </>
  );

  return (
    <>
      <a className="gw-skip-link" href="#main-content">
        {t("nav.skipToContent")}
      </a>

      <header className="gw-header" data-scrolled={String(scrolled)}>
        <div
          className="gw-scroll-progress"
          style={{ transform: `scaleX(${scrollProgress})` }}
          aria-hidden="true"
        />
        <div className="gw-container gw-header__inner">
          <BrandLink />

          <nav className="gw-nav" aria-label={t("nav.primary")}>
            {renderLinks()}
          </nav>

          <LanguageSwitcher />

          <button
            ref={toggleRef}
            type="button"
            className="gw-nav-toggle"
            aria-expanded={menuOpen}
            aria-controls="mobile-navigation"
            aria-label={menuOpen ? t("nav.closeMenu") : t("nav.openMenu")}
            onClick={() => setMenuOpen((open) => !open)}
          >
            <svg
              viewBox="0 0 24 24"
              width="22"
              height="22"
              aria-hidden="true"
              focusable="false"
            >
              {menuOpen ? (
                <path
                  d="M6 6l12 12M18 6L6 18"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  fill="none"
                />
              ) : (
                <path
                  d="M4 7h16M4 12h16M4 17h16"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  fill="none"
                />
              )}
            </svg>
          </button>
        </div>

        {menuOpen ? (
          <div className="gw-container">
            <nav
              id="mobile-navigation"
              className="gw-drawer"
              aria-label={t("nav.primary")}
            >
              {renderLinks(() => setMenuOpen(false))}
            </nav>
          </div>
        ) : null}
      </header>

      {/* tabIndex -1 makes the landmark focusable for the post-navigation
          focus move above, without putting it in the tab order. */}
      <main id="main-content" tabIndex={-1}>
        <Outlet />
      </main>

      <SiteFooter />
    </>
  );
};

const SiteFooter = () => {
  const { t, path } = useLanguage();
  const year = new Date().getFullYear();

  return (
    <footer className="gw-footer">
      <div className="gw-container">
        <div className="gw-footer__grid">
          <div>
            <Link to={path("/")} className="gw-footer__logo">
              <img
                src={footerLogoUrl}
                alt={t("meta.siteName")}
                width={841}
                height={198}
              />
            </Link>
          </div>

          <div>
            <h2 className="gw-footer__heading">{t("footer.explore")}</h2>
            <ul className="gw-footer__list">
              <li>
                <Link to={path("/about")}>{t("nav.about")}</Link>
              </li>
              <li>
                <Link to={path("/services")}>{t("nav.services")}</Link>
              </li>
              <li>
                <Link to={path("/check-in")}>{t("nav.assessment")}</Link>
              </li>
              <li>
                <Link to={path("/partnerships")}>{t("nav.partnerships")}</Link>
              </li>
              <li>
                <Link to={path("/careers")}>{t("nav.careers")}</Link>
              </li>
              <li>
                <Link to={path("/contact")}>{t("nav.contact")}</Link>
              </li>
            </ul>
          </div>

          <div>
            <h2 className="gw-footer__heading">{t("footer.legal")}</h2>
            <ul className="gw-footer__list">
              <li>
                <Link to={path("/privacy")}>{t("footer.privacy")}</Link>
              </li>
              <li>
                <Link to={path("/terms")}>{t("footer.terms")}</Link>
              </li>
              <li>
                <Link to={path("/account")}>{t("nav.account")}</Link>
              </li>
            </ul>
          </div>
        </div>

        {/* The crisis note is given its own prominent block rather than
            being buried in small print, because it is the one piece of
            footer content someone may urgently need. */}
        <div
          style={{
            marginTop: "2.5rem",
            padding: "1rem 1.25rem",
            borderRadius: "12px",
            background: "rgb(255 255 255 / 8%)",
          }}
        >
          <h2 className="gw-footer__heading" style={{ marginBottom: "0.4rem" }}>
            {t("footer.crisisHeading")}
          </h2>
          <p style={{ margin: 0 }}>{t("footer.crisisBody")}</p>
        </div>

        <div className="gw-footer__bottom">
          <span>
            © {year} {t("meta.siteName")}. {t("footer.rights")}
          </span>
          <span style={{ maxWidth: "60ch" }}>{t("footer.notMedical")}</span>
        </div>
      </div>
    </footer>
  );
};
